import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import * as api from './elasticsearchAPI';
import { getGlobalAbortControllerById } from '../../utils/abortController';
import {fetchObjects} from "../objects/objectsSlice";
import { updateUserSettings } from '../account/accountSlice';

const initialState = {};

// This is just used as a default value when state[key] (and state[key].status etc.) is not yet initialized.
export const SEARCH_OBJECT_TYPES = ['folder', 'document', 'journal', 'metric'];
export const SEARCH_OBJECT_TYPES_SETTING_NAME = 'search_object_types';

const defaultValues = {
  status: 'idle',
  searchTs: 0,
  objects: [],
  q: "",
  from: 0,
  size: 10,
  total: 0,
  currentPage: 1,
  showResults: false,
  searchMade: false,
  searchInCurrentFolder: false
};

export const searchObjects = createAsyncThunk(
  'objects/searchObjects',
  async (params, { dispatch }) => {
    const globalAbortController = getGlobalAbortControllerById('elasticsearchSlice.searchObjects');
    const response = await api.searchObjects(params, globalAbortController);

    let idList = null;
    if (response?.body?.results) {
      idList = response.body.results.map((item) => item.id);
    }
    
    if (idList) {
      dispatch(fetchObjects({idList: idList, append: true, withBreadcrumb: true}));
    }

    return response?.body;
  }
);

export const searchObjectsInFolder = createAsyncThunk(
  'objects/searchObjectsInFolder',
  async (params, { dispatch }) => {
    const globalAbortController = getGlobalAbortControllerById('elasticsearchSlice.searchObjectsInFolder');
    const response = await api.searchObjectsInFolder(params, globalAbortController);

    let idList = null;
    if (response?.body?.results) {
      idList = response.body.results.map((item) => item.id);
    }
    
    if (idList) {
      dispatch(fetchObjects({idList: idList, append: true, withBreadcrumb: true}));
    }

    return response?.body;
  }
);

/**
 * Identifier mechanism is used to distinguish between different searches on the same page.
 * In objects-list-view we have "normal" search and "move" search. Move search is used to search for folders to move objects to.
 * Identifier thing works automatically, you just need to make up some string to identifier prop. Like this:
 * <Elasticsearch identifier="search" />
 */

export const elasticsearchSlice = createSlice({
  name: 'elasticsearch',
  initialState,
  reducers: {
    invalidateObjects: (state, action) => {
      const { identifier, value } = action.payload || {};
      if (state[identifier]) {
        state[identifier].objects = [];
        state[identifier].searchMade = false;
        state[identifier].from = 0;
        state[identifier].total = 0;
        state[identifier].currentPage = 0;
        state[identifier].showResults = value;
      }
    },
    resetAll: () => {
      return {};
    },
    setShowResults: (state, action) => {
      const { identifier, value } = action.payload || {};
      if (!state[identifier]) {
        state[identifier] = {};
      }

      state[identifier].showResults = value;
    },
    setQ: (state, action) => {
      const { identifier, value } = action.payload || {};
      if (!state[identifier]) {
        state[identifier] = {};
      }

      state[identifier].q = value;
    },
    setStatus: (state, action) => {
      const { identifier, value } = action.payload || {};
      if (!state[identifier]) {
        state[identifier] = {};
      }

      state[identifier].status = value;
    },
    setSearchInCurrentFolder: (state, action) => {
      const { identifier, value } = action.payload || {};
      if (!state[identifier]) {
        state[identifier] = {};
      }
      state[identifier].searchInCurrentFolder = value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchObjects.pending, (state, action) => {
        const { identifier } = action.meta?.arg || {};
        if (!state[identifier]) {
          state[identifier] = {};
        }
        state[identifier].status = 'searching';
      })
      .addCase(searchObjects.fulfilled, (state, action) => {
        if (!action?.payload) {
          // Request was aborted
          return;
        }
        const { identifier } = action.meta?.arg || {};
        if (!state[identifier]) {
          state[identifier] = {};
        }

        state[identifier].status = 'idle';

        if (action.payload.fetched_ts < state[identifier].searchTs) {
          return;
        }

        state[identifier].searchTs = action.payload.fetched_ts;
        state[identifier].objects = action.payload.results;
        state[identifier].searchMade = true;
        state[identifier].total = action.payload.total;
        state[identifier].from = action.meta.arg.from || 0;
        state[identifier].currentPage = action.meta.arg.page || 1;
        state[identifier].showResults = true;
      })
      .addCase(searchObjects.rejected, (state, action) => {
        const { identifier } = action.meta?.arg || {};
        if (!state[identifier]) {
          state[identifier] = {};
        }

        state[identifier].status = 'error';
      })
      .addCase(searchObjectsInFolder.pending, (state, action) => {
        const { identifier } = action.meta?.arg || {};
        if (!state[identifier]) {
          state[identifier] = {};
        }
        state[identifier].status = 'searching';
      })
      .addCase(searchObjectsInFolder.fulfilled, (state, action) => {
        if (!action?.payload) {
        // Request was aborted
          return;
        }
        const { identifier } = action.meta?.arg || {};
        if (!state[identifier]) {
          state[identifier] = {};
        }

        state[identifier].status = 'idle';

        if (action.payload.fetched_ts < state[identifier].searchTs) {
          return;
        }

        state[identifier].searchTs = action.payload.fetched_ts;
        state[identifier].objects = action.payload.results;
        state[identifier].searchMade = true;
        state[identifier].total = action.payload.total;
        state[identifier].from = action.meta.arg.from || 0;
        state[identifier].currentPage = action.meta.arg.page || 1;
        state[identifier].showResults = true;
      })
      .addCase(searchObjectsInFolder.rejected, (state, action) => {
        const { identifier } = action.meta?.arg || {};
        if (action.error.name === 'AbortError') {
          return; // Ignore AbortError
        }
      
        if (!state[identifier]) {
          state[identifier] = {};
        }

        state[identifier].status = 'error';
      });
  },
});

export const {
  invalidateObjects,
  setShowResults,
  resetAll,
  setQ,
  setStatus,
  setSearchInCurrentFolder

} = elasticsearchSlice.actions;

/**
 * Thunk that handles object-type toggle: persists the setting and re-triggers
 * a global search if one is active. Reads all needed state internally so the
 * calling component does not need to subscribe to search selectors or userId.
 */
export const toggleObjectTypeAndSearch = ({ identifier, searchFolders, folderId, objectTypes }) =>
  (dispatch, getState) => {
    const state = getState();
    const userId = state.auth.data.user_id;

    dispatch(updateUserSettings({
      id: userId,
      name: SEARCH_OBJECT_TYPES_SETTING_NAME,
      value: objectTypes.join(','),
      propertyName: "searchObjectTypes"
    }));

    const es = state.elasticsearch[identifier];
    if (!es) return;

    const q = es.q || defaultValues.q;
    const searchMade = es.searchMade || defaultValues.searchMade;
    const searchInCurrentFolder = es.searchInCurrentFolder || defaultValues.searchInCurrentFolder;

    if (q && searchMade) {
      if (searchInCurrentFolder) {
        dispatch(searchObjectsInFolder({
          identifier,
          q,
          from: es.from || defaultValues.from,
          size: es.size || defaultValues.size,
          page: es.currentPage || defaultValues.currentPage,
          folderId,
          objectTypes,
        }));
      } else {
        dispatch(searchObjects({
          identifier,
          q,
          from: es.from || defaultValues.from,
          size: es.size || defaultValues.size,
          page: es.currentPage || defaultValues.currentPage,
          searchFolders,
          objectTypes,
        }));
      }
    }
  };

export const selectStatus = (state, identifier) => state.elasticsearch[identifier]?.status || defaultValues.status;
export const selectShowResults = (state, identifier) => state.elasticsearch[identifier]?.showResults || defaultValues.showResults;
export const selectObjects = (state, identifier) => state.elasticsearch[identifier]?.objects || defaultValues.objects;
export const selectSearchMade = (state, identifier) => state.elasticsearch[identifier]?.searchMade || defaultValues.searchMade;
export const selectQ = (state, identifier) => state.elasticsearch[identifier]?.q || defaultValues.q;
export const selectFrom = (state, identifier) => state.elasticsearch[identifier]?.from || defaultValues.from;
export const selectSize = (state, identifier) => state.elasticsearch[identifier]?.size || defaultValues.size;
export const selectTotal = (state, identifier) => state.elasticsearch[identifier]?.total || defaultValues.total;
export const selectCurrentPage = (state, identifier) => state.elasticsearch[identifier]?.currentPage || defaultValues.currentPage;
export const selectSearchInCurrentFolder = (state, identifier) => state.elasticsearch[identifier]?.searchInCurrentFolder || defaultValues.searchInCurrentFolder;

// Metric ids among the search results, so latest-measurements is queried with the
// exact rows it needs. Type is read from objectsMapById (populated by the search
// thunk's fetchObjects dispatch), which is what the result rows render from.
// Factory + per-instance memoization keeps results stable across search identifiers.
export const makeSelectMetricObjectIds = () => createSelector(
  [
    (state, identifier) => state.elasticsearch[identifier]?.objects || defaultValues.objects,
    (state) => state.objects.objectsMapById,
  ],
  (objects, objectsMapById) =>
    objects.filter(o => objectsMapById[o.id]?.type === 'metric').map(o => o.id)
);

export default elasticsearchSlice.reducer;